import React, { useState, useEffect } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Fade,
  Typography,
  Container,
  Menu,
  MenuItem,
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Close as CloseIcon, 
  Phone, 
  Language,
  ArrowDropDown
} from '@mui/icons-material';
import LoadingImage from './LoadingImage';

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState<null | HTMLElement>(null);
  const [isServicesHovered, setIsServicesHovered] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navItems = [
    { path: '/', label: 'Anasayfa' },
    { path: '/hakkimizda', label: 'Hakkımızda' },
    { path: '/hizmetler', label: 'Hizmetlerimiz', hasDropdown: true },
    { path: '/iletisim', label: 'İletişim' },
  ];

  const servicesItems = [
    { path: '/hizmetler', label: 'Hizmetlerimiz' },
    { path: '/hizmetler/yedek-parca', label: 'Yedek Parça' },
    { path: '/hizmetler/arac-ici-yalitim', label: 'Araç İçi Yalıtım' },
    { path: '/hizmetler/double-deck', label: 'Çift Katlı Sistem (Double Deck System)' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleServicesMenuClose = () => {
    setServicesAnchorEl(null);
  };


  const handleServicesMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (!isMobile) {
      setIsServicesHovered(true);
      setServicesAnchorEl(event.currentTarget);
    }
  };

  const handleServicesMouseLeave = () => {
    // Don't close immediately, let useEffect handle it
    setIsServicesHovered(false);
  };

  const handleMenuMouseEnter = () => {
    if (!isMobile) {
      setIsServicesHovered(true);
    }
  };

  const handleMenuMouseLeave = () => {
    if (!isMobile) {
      setIsServicesHovered(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  // Close menu when not hovering
  useEffect(() => {
    if (!isServicesHovered && servicesAnchorEl) {
      const timeout = setTimeout(() => {
        setServicesAnchorEl(null);
      }, 1500); // 1 second delay to prevent flickering
      return () => clearTimeout(timeout);
    }
  }, [isServicesHovered, servicesAnchorEl]);

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => (
          <React.Fragment key={item.path}>
            <ListItem
              component={RouterLink}
              to={item.path}
              onClick={handleDrawerToggle}
              sx={{
                color: isActive(item.path) ? 'primary.main' : 'text.primary',
                bgcolor: isActive(item.path) ? 'primary.50' : 'transparent',
                fontWeight: isActive(item.path) ? 600 : 400,
                '&:hover': {
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                },
              }}
            >
              <ListItemText primary={item.label} />
            </ListItem>
            {item.hasDropdown && (
              <>
                {servicesItems.map((subItem, index) => (
                  <ListItem
                    key={subItem.path}
                    component={RouterLink}
                    to={subItem.path}
                    onClick={handleDrawerToggle}
                    sx={{
                      pl: 4,
                      color: isActive(subItem.path) ? 'primary.main' : 'text.secondary',
                      bgcolor: isActive(subItem.path) ? 'primary.50' : index === 0 ? 'primary.25' : 'transparent',
                      fontWeight: isActive(subItem.path) ? 600 : index === 0 ? 600 : 400,
                      borderBottom: index === 0 ? '1px solid' : 'none',
                      borderColor: index === 0 ? 'grey.200' : 'transparent',
                      '&:hover': {
                        bgcolor: 'primary.50',
                        color: 'primary.main',
                      },
                    }}
                  >
                    <ListItemText primary={subItem.label} />
                  </ListItem>
                ))}
              </>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* Top Contact Bar */}
      <Fade in>
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: 0.5,
            display: { xs: 'none', md: 'block' },
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 14 }} />
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    Bize Ulaşın: +90 545 288 72 93&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;+90 553 181 23 95 
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Language sx={{ fontSize: 14 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Türkçe
                </Typography>
                <ArrowDropDown sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          </Container>
        </Box>
      </Fade>

      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          bgcolor: 'white',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          transition: 'all 0.3s ease',
          top: { xs: 0, md: '32px' },
          zIndex: 1200,
          overflow: 'hidden', // ensure no child overflows on small screens
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ 
            py: { xs: 0.5, md: 1 }, 
            minHeight: { xs: '56px !important', md: '64px !important' },
            px: { xs: 1, md: 2 },
            overflow: 'hidden',
          }}>
            {/* Logo */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                mr: { xs: 1.5, md: 4 },
              }}
            >
              <Box sx={{ height: { xs: 16, sm: 20, md: 40 }, width: 'auto', display: 'flex', alignItems: 'center', mr: { xs: 0.75, sm: 1.25, md: 2 } }}>
                <LoadingImage
                  src="/logo1.jpeg"
                  alt="Kale Treyler Logo"
                  style={{
                    height: '100%',
                    width: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                KALE TREYLER FRİGO
              </Typography>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
            </Box>

            {/* Navigation */}
            {isMobile ? (
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  ml: 'auto',
                  color: 'text.primary',
                }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, justifyContent: 'center' }}>
                  {navItems.map((item) => (
                    item.hasDropdown ? (
                      <Button
                        key={item.path}
                        onMouseEnter={handleServicesMouseEnter}
                        onMouseLeave={handleServicesMouseLeave}
                        sx={{
                          textTransform: 'none',
                          fontWeight: isActive(item.path) || location.pathname.startsWith('/hizmetler') ? 600 : 400,
                          color: isActive(item.path) || location.pathname.startsWith('/hizmetler') ? 'primary.main' : 'text.primary',
                          bgcolor: isActive(item.path) || location.pathname.startsWith('/hizmetler') ? 'primary.50' : 'transparent',
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          position: 'relative',
                          '&:hover': {
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        endIcon={<ArrowDropDown />}
                      >
                        {item.label}
                      </Button>
                    ) : (
                      <Button
                        key={item.path}
                        component={RouterLink}
                        to={item.path}
                        sx={{
                          textTransform: 'none',
                          fontWeight: isActive(item.path) ? 600 : 400,
                          color: isActive(item.path) ? 'primary.main' : 'text.primary',
                          bgcolor: isActive(item.path) ? 'primary.50' : 'transparent',
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          position: 'relative',
                          '&:hover': {
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {item.label}
                      </Button>
                    )
                  ))}
                </Box>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Services Dropdown Menu */}
      <Menu
        anchorEl={servicesAnchorEl}
        open={Boolean(servicesAnchorEl)}
        onClose={handleServicesMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'grey.200',
            minWidth: 250,
          },
        }}
      >
        <Box
          onMouseEnter={handleMenuMouseEnter}
          onMouseLeave={handleMenuMouseLeave}
        >
          {servicesItems.map((item, index) => (
            <MenuItem
              key={item.path}
              component={RouterLink}
              to={item.path}
              onClick={handleServicesMenuClose}
              sx={{
                color: isActive(item.path) ? 'primary.main' : 'text.primary',
                fontWeight: isActive(item.path) ? 600 : 400,
                bgcolor: index === 0 ? 'primary.50' : 'transparent',
                borderBottom: index === 0 ? '1px solid' : 'none',
                borderColor: index === 0 ? 'grey.200' : 'transparent',
                '&:hover': {
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {item.label}
            </MenuItem>
          ))}
        </Box>
      </Menu>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* AppBar için spacing */}
      <Toolbar sx={{ minHeight: { xs: '56px', md: '96px' } }} />
    </>
  );
};

export default Navbar; 